import { Link } from 'react-router'
import { PROVIDERS } from '@/providers'
import { STATUS_LINKS } from '@/status/products'

/**
 * The rest of the site, on every page, plus the line that has to be on every page.
 *
 * Three columns and a disclaimer. The disclaimer is first because it is load-bearing — the whole
 * site is parody and R-906 wants "not affiliated" in the body of every indexable page, not tucked
 * under a heap of links.
 *
 * The columns exist because until F31 the seven status pages were orphans: nothing linked to
 * `/status/openai` from anywhere, and a page no page links to is a page a crawler reaches once and
 * then forgets. The header's Status menu is the same seven, and on a phone it collapses to one
 * link — which is exactly why the full list has to be down here, where there is room for it.
 *
 * Anchor text is the phrase, never the bare brand: "ChatGPT status", not "ChatGPT"
 * (`docs/seo/keywords.md`). Anchor text is the strongest thing one page can say about another, and
 * a column of company names says nothing about the question each page answers.
 */
export function Footer() {
  return (
    <footer className="mha-footer">
      {/* The disclaimer is load-bearing, not decoration: the whole site is parody and this
          line has to stay visible on every page (CLAUDE.md, standing rules). */}
      <p className="mha-footer__note">
        <strong>Not affiliated</strong> with OpenAI, Anthropic or Google. Names, signs and buildings
        on this site are parody. Status comes from each provider&rsquo;s own public status page.
      </p>

      <nav className="mha-footer__nav" aria-label="Footer">
        <div className="mha-footer__col">
          <h2 className="mha-footer__title">Status</h2>
          <ul className="mha-footer__list">
            {STATUS_LINKS.map((link) => (
              <li key={link.to}>
                <Link className="mha-footer__link" to={link.to}>
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="mha-footer__col">
          <h2 className="mha-footer__title">Site</h2>
          <ul className="mha-footer__list">
            <li>
              <Link className="mha-footer__link" to="/">
                Live
              </Link>
            </li>
            <li>
              <Link className="mha-footer__link" to="/history">
                AI outage history
              </Link>
            </li>
            <li>
              <Link className="mha-footer__link" to="/codex">
                Codex resets
              </Link>
            </li>
            <li>
              <Link className="mha-footer__link" to="/about">
                About
              </Link>
            </li>
            <li>
              <Link className="mha-footer__link" to="/api-docs">
                API
              </Link>
            </li>
            <li>
              {/* Served by the host, not the router, so an ordinary anchor: a `<Link>` here would
                  push a route react-router has no page for and answer with the not-found view. */}
              <a className="mha-footer__link" href="/feed.xml">
                Feed
              </a>
            </li>
          </ul>
        </div>

        <div className="mha-footer__col">
          <h2 className="mha-footer__title">Sources</h2>
          <ul className="mha-footer__list">
            {PROVIDERS.map((provider) => (
              <li key={provider.id}>
                {/*
                  `nofollow` beside `noopener`: these are the pages this site is downstream of and
                  says so on every status page, but they are also the three outbound links on every
                  page of the site, and an unqualified site-wide link to somebody else's dashboard
                  is a footer that exists to pass ranking rather than to help a reader.
                */}
                <a
                  className="mha-footer__link"
                  href={provider.statusUrl}
                  rel="noopener nofollow"
                  target="_blank"
                >
                  {provider.label} status page
                  <span aria-hidden="true"> ↗</span>
                  <span className="sr-only"> (opens in a new tab)</span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      </nav>
    </footer>
  )
}
